import Container from '@/components/ui/container';
import InputUi from '@/components/ui/inputui';
import { Button } from '@/components/ui/button';
import React, { useState } from 'react';
import Layout from 'src/layout';
import { createCoupon } from '../../lib/api/couponsApi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { RiCouponLine, RiArrowLeftLine, RiPriceTag3Line } from 'react-icons/ri';

const AddCoupon = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        couponCode: '',
        assignedUser: '',
        usageLimit: '',
        discountType: 'percentage',
        discountValue: '',
        maxUsagePerUser: '',
        minOrderValue: '',
    });

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async () => {
        if (!formData.couponCode.trim()) {
            toast.error('Coupon Code is required');
            return;
        }
        if (!formData.discountType.trim()) {
            toast.error('Discount Type is required');
            return;
        }
        if (!formData.discountValue || Number(formData.discountValue) <= 0) {
            toast.error('Valid Discount Value is required');
            return;
        }
        if (!formData.usageLimit || Number(formData.usageLimit) <= 0) {
            toast.error('Global Usage Limit is required');
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                ...formData,
                couponCode: formData.couponCode.trim().toUpperCase(),
                usageLimit: Number(formData.usageLimit),
                discountValue: Number(formData.discountValue),
            };

            if (!formData.assignedUser) {
                delete payload.assignedUser;
            }
            if (!formData.maxUsagePerUser) {
                delete payload.maxUsagePerUser;
            } else {
                payload.maxUsagePerUser = Number(formData.maxUsagePerUser);
            }
            if (!formData.minOrderValue) {
                delete payload.minOrderValue;
            } else {
                payload.minOrderValue = Number(formData.minOrderValue);
            }

            const response = await createCoupon(payload);

            if (response?.result?.success || response?.success) {
                toast.success('Coupon created successfully!');
                navigate('/coupons/list');
            } else {
                toast.error(response?.result?.message || response?.message || 'Something went wrong');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to create coupon');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout title={'Add Coupon'} active={'admin-coupons-add'}>
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <button
                                onClick={() => navigate('/coupons/list')}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-orange-600 mb-2 transition-colors"
                            >
                                <RiArrowLeftLine className="w-4 h-4" /> Back to Coupons
                            </button>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiCouponLine className="text-orange-500" /> Create New Coupon
                            </h1>
                            <p className="text-slate-500 text-sm mt-0.5">
                                Set discount codes, user targeting rules, and order value thresholds.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/coupons/list')}
                                className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="bg-orange-600 hover:bg-orange-700 text-white font-medium shadow-sm min-w-[130px]"
                            >
                                {submitting ? 'Creating...' : 'Create Coupon'}
                            </Button>
                        </div>
                    </div>

                    {/* Form Body Card */}
                    <div className="max-w-4xl bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-6">
                        <h2 className="text-base font-semibold text-slate-900 pb-3 border-b border-slate-100 flex items-center gap-2">
                            <RiPriceTag3Line className="text-orange-500" /> Coupon Configuration
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <InputUi
                                    label="Coupon Code *"
                                    placeholder="e.g. FESTIVE20"
                                    value={formData.couponCode}
                                    datafunction={handleChange('couponCode')}
                                />
                                <p className="text-xs text-slate-400 mt-1">Codes are automatically saved in uppercase.</p>
                            </div>

                            <div>
                                <InputUi
                                    label="Assigned User Email / Phone (Optional)"
                                    placeholder="Leave empty for public use"
                                    value={formData.assignedUser}
                                    datafunction={handleChange('assignedUser')}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                    Discount Type *
                                </label>
                                <select
                                    className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                                    value={formData.discountType}
                                    onChange={handleChange('discountType')}
                                >
                                    <option value="percentage">Percentage (%) Discount</option>
                                    <option value="flat">Flat Amount (₹) Discount</option>
                                </select>
                            </div>

                            <div>
                                <InputUi
                                    label={`Discount Value (${formData.discountType === 'percentage' ? '%' : '₹'}) *`}
                                    type="number"
                                    placeholder={formData.discountType === 'percentage' ? 'e.g. 15' : 'e.g. 500'}
                                    value={formData.discountValue}
                                    datafunction={handleChange('discountValue')}
                                />
                            </div>

                            <div>
                                <InputUi
                                    label="Global Usage Limit *"
                                    type="number"
                                    placeholder="Total times code can be redeemed"
                                    value={formData.usageLimit}
                                    datafunction={handleChange('usageLimit')}
                                />
                            </div>

                            <div>
                                <InputUi
                                    label="Max Usage Per User (Optional)"
                                    type="number"
                                    placeholder="Default: Unlimited"
                                    value={formData.maxUsagePerUser}
                                    datafunction={handleChange('maxUsagePerUser')}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <InputUi
                                    label="Minimum Order Value (₹, Optional)"
                                    type="number"
                                    placeholder="Minimum cart total required to apply coupon"
                                    value={formData.minOrderValue}
                                    datafunction={handleChange('minOrderValue')}
                                />
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        </Layout>
    );
};

export default AddCoupon;
